import React, { useState } from "react";
import { PackageSearch, MessageCircle, ArrowRight, CheckCircle2, User, Phone, Globe, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Combobox } from "../components/Combobox";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { trackText } from "../waHelper";

const empty = { trackingNumber: "", name: "", phone: "", country: "" };

export default function Track() {
  const { user, countries, t } = useApp();
  const [f, setF] = useState({ ...empty, name: user?.name || "", phone: user?.phone || "" });
  const [done, setDone] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const options = countries.map((c) => ({ value: c.name, label: c.name, flag: c.flag }));

  const buildWaText = () => encodeURIComponent(trackText(f));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.trackingNumber) { toast.error("Please enter your package / tracking number"); return; }
    try {
      const res = await api.track(f);
      const chosen = res?.whatsapp;
      setWaNumber(chosen);
      setDone(true);
      toast.success("Opening WhatsApp for your tracking update…");
      window.open(`https://wa.me/${chosen}?text=${buildWaText()}`, "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start tracking. Please try again.");
    }
  };

  if (done) {
    return (
      <section className="max-w-2xl mx-auto px-5 py-24 text-center animate-fade-up" data-testid="track-success">
        <div className="w-16 h-16 rounded-2xl orange-gradient grid place-items-center mx-auto orange-glow"><CheckCircle2 className="w-9 h-9 text-white" /></div>
        <h1 className="font-display font-bold text-3xl mt-6">Tracking request received!</h1>
        <div className="glass rounded-2xl p-5 mt-6 flex items-center gap-3 text-left" data-testid="track-ack">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500" />
          </span>
          <p className="text-sm text-neutral-700"><span className="font-semibold">We're checking your package now.</span> A team member is reviewing package <span className="font-semibold">#{f.trackingNumber}</span> and will reply on WhatsApp shortly with the latest status.</p>
        </div>
        <p className="text-neutral-600 mt-4">We've opened WhatsApp with your details — just hit send and we'll take it from there.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-8">
          <Button data-testid="track-reopen-whatsapp" className="orange-gradient text-white border-0 gap-2 hover:opacity-90" onClick={() => window.open(`https://wa.me/${waNumber}?text=${buildWaText()}`, "_blank")}><MessageCircle className="w-4 h-4" /> Reopen WhatsApp</Button>
          <Button data-testid="track-another" variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04]" onClick={() => { setF({ ...empty, name: user?.name || "", phone: user?.phone || "" }); setDone(false); }}>Track another</Button>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="relative grid-bg border-b border-black/10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-16 relative animate-fade-up">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">{t("track.eyebrow")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-3">{t("track.title")}</h1>
          <p className="text-neutral-600 mt-4 max-w-2xl text-lg">{t("track.subtitle")}</p>
        </div>
      </section>

      <form onSubmit={submit} className="max-w-2xl mx-auto px-5 py-14 space-y-8" data-testid="track-form">
        <div className="glass rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1"><PackageSearch className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Your tracking details</h2></div>

          <div>
            <Label className="text-neutral-700 text-sm">Package / tracking number <span className="text-orange-500">*</span></Label>
            <Input data-testid="track-number-input" value={f.trackingNumber} onChange={set("trackingNumber")} placeholder="e.g. SL-2026-00123" className="bg-black/[0.03] border-black/10 mt-1.5" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-neutral-700 text-sm flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Full name</Label>
              <Input data-testid="track-name-input" value={f.name} onChange={set("name")} placeholder="Your name" className="bg-black/[0.03] border-black/10 mt-1.5" />
            </div>
            <div>
              <Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Phone number</Label>
              <Input data-testid="track-phone-input" value={f.phone} onChange={set("phone")} placeholder="+39 …" className="bg-black/[0.03] border-black/10 mt-1.5" />
            </div>
          </div>

          <div>
            <Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Your country</Label>
            <div className="mt-1.5"><Combobox options={options} value={f.country} onChange={set("country")} placeholder="Search your country…" /></div>
            <p className="text-xs text-neutral-500 mt-1.5">We use this to route you to the team handling your region.</p>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg orange-gradient grid place-items-center shrink-0"><Clock className="w-5 h-5 text-white" /></div>
          <div><p className="font-semibold text-sm">Anytime status updates</p><p className="text-sm text-neutral-600 mt-1">Send us your package number and a team member will reply on WhatsApp with the latest location and delivery status.</p></div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between glass rounded-2xl p-6">
          <p className="text-sm text-neutral-600">Your tracking request opens a WhatsApp chat with our team for a live reply.</p>
          <Button data-testid="track-submit-button" type="submit" className="orange-gradient text-white border-0 h-12 px-8 gap-2 hover:opacity-90 shrink-0">{t("track.submit")} <ArrowRight className="w-4 h-4" /></Button>
        </div>
      </form>
    </div>
  );
}
